import { useEffect, useState } from "react";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import { api, deriveKey } from "../app/api";
import { useStore } from "../app/store";
import { Button, useToast } from "../ui/kit";
import { totals } from "../app/stats";

type Mode = "login" | "signup" | "recover";

function next(): string {
  const n = new URLSearchParams(location.search).get("next");
  return n && n.startsWith("/") && !n.startsWith("//") ? n : "/today";
}

export function RecoveryKeyCard({ recoveryKey, onDone, doneLabel = "I saved it, continue" }: { recoveryKey: string; onDone: () => void; doneLabel?: string }) {
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  return (
    <div className="recovery">
      <p>
        This is your <strong>recovery key</strong>. Ponder doesn't send e-mails, so it's the only way back in if you forget your password.
        Keep it somewhere safe. It's shown only once.
      </p>
      <code className="recovery-key">{recoveryKey}</code>
      <div className="row-gap">
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(recoveryKey);
              toast("Recovery key copied");
              setSaved(true);
            } catch {
              toast("Select the key and copy it by hand.", "error");
            }
          }}
        >
          Copy
        </Button>
        <Button
          onClick={() => {
            const blob = new Blob([`Ponder recovery key\n\n${recoveryKey}\n\nUse it at ${location.origin}/recover if you forget your password.\n`], { type: "text/plain" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "ponder-recovery-key.txt";
            a.click();
            setSaved(true);
          }}
        >
          Save as file
        </Button>
      </div>
      <label className="check-row">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>I've kept my recovery key somewhere safe</span>
      </label>
      <Button kind="primary" disabled={!saved} onClick={onDone}>
        {doneLabel}
      </Button>
    </div>
  );
}

export function AuthPage({ mode }: { mode: Mode }) {
  const { user, setUser } = useSession();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [recovery, setRecovery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const t = useStore((d) => totals(d));

  useEffect(() => {
    document.title = (mode === "login" ? "Sign in" : mode === "signup" ? "Create account" : "Recover account") + " · Ponder";
  }, [mode]);

  useEffect(() => {
    if (user && !newKey) navigate(next(), { replace: true });
  }, [user, newKey]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (mode !== "login" && password.length < 8) {
      setErr("Use at least 8 characters for your password.");
      return;
    }
    setBusy(true);
    try {
      const key = await deriveKey(password, email);
      if (mode === "login") {
        const r = await api.login(email, key);
        setUser(r.user);
      } else if (mode === "signup") {
        const r = await api.signup(email, name, key);
        setNewKey(r.recoveryKey);
        setUser(r.user);
      } else {
        const r = await api.recover(email, recovery, key);
        setNewKey(r.recoveryKey);
        setUser(r.user);
      }
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <Link to="/welcome" className="brand auth-brand">
        <img src="/favicon-64.png" alt="" width="36" height="36" />
        <span className="wordmark">ponder</span>
      </Link>
      <div className="auth-card card">
        {newKey ? (
          <>
            <h1>{mode === "recover" ? "You're back in" : "Welcome to Ponder!"}</h1>
            <RecoveryKeyCard recoveryKey={newKey} onDone={() => { setNewKey(null); navigate(next(), { replace: true }); }} />
          </>
        ) : (
          <form onSubmit={submit} className="auth-form">
            <h1>{mode === "login" ? "Welcome back" : mode === "signup" ? "Keep your thinking safe" : "Recover your account"}</h1>
            {mode === "signup" && (
              <p className="muted">
                A free account syncs your puzzles, streak and notes between all your devices.
                {t.solved + Math.floor(t.seconds / 60) > 0 ? " What you've done on this device comes with you." : ""}
              </p>
            )}
            {mode === "signup" && (
              <label className="field">
                <span>Your name</span>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Ada" maxLength={60} />
              </label>
            )}
            <label className="field">
              <span>E-mail</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" required placeholder="you@example.com" autoCapitalize="none" />
            </label>
            {mode === "recover" && (
              <label className="field">
                <span>Recovery key</span>
                <input type="text" value={recovery} onChange={(e) => setRecovery(e.target.value)} required placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" autoCapitalize="characters" autoComplete="off" />
              </label>
            )}
            <label className="field">
              <span>{mode === "recover" ? "New password" : "Password"}</span>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={mode === "login" ? 1 : 8} placeholder={mode === "login" ? "" : "at least 8 characters"} />
            </label>
            {err && <p className="form-error" role="alert">{err}</p>}
            <Button kind="primary" size="big" type="submit" disabled={busy}>
              {busy ? "One moment…" : mode === "login" ? "Sign in" : mode === "signup" ? "Create account" : "Reset password"}
            </Button>
            <div className="auth-links">
              {mode === "login" ? (
                <>
                  <Link to={"/signup" + location.search}>New here? Create an account</Link>
                  <Link to="/recover">Forgot password?</Link>
                </>
              ) : (
                <Link to={"/login" + (mode === "signup" ? location.search : "")}>I already have an account</Link>
              )}
            </div>
            {mode === "signup" && <p className="muted small">No e-mails, no ads, no tracking. Your password is scrambled on this device before it is sent.</p>}
          </form>
        )}
      </div>
    </main>
  );
}
